/**
 * Custom Infinite Scroll (Scrollify-like)
 * - Mode 1: auto load on scroll
 * - Mode 2: load more button
 * - Uses #gscollectionbottom / #gsloadmore / #gsloaderimg like the pasted script
 * - Persists loaded HTML in sessionStorage and restores when returning from a product page
 * - Preserves existing query params (sort_by, view, q, constraint, etc.) while changing page=
 * - Adds <meta name="robots" content="noindex"> when ?page= is present (like your paste)
 *
 * Setup:
 * 1) Add this file to assets and include on collection/search templates.
 * 2) (Recommended) Set selectors via window.InfiniteScrollConfig (see bottom).
 */
(function () {
  "use strict";

  const STORAGE = {
    collectionUrl: "gscollectionurl",
    collectionContent: "gscollectioncontent",
    paginateContent: "gspaginatecontent",
    pageNr: "gscollectionpagenr",
    productViewed: "gsproductviewed",
  };

  function safeGetSession(key) {
    try {
      return sessionStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }
  function safeSetSession(key, value) {
    try {
      sessionStorage.setItem(key, value);
    } catch (e) {}
  }

  function stripPageParam(urlString) {
    try {
      const u = new URL(urlString, window.location.origin);
      u.searchParams.delete("page");
      u.search = u.searchParams.toString()
        ? `?${u.searchParams.toString()}`
        : "";
      return u.toString();
    } catch (e) {
      return urlString;
    }
  }

  function currentIsProductPage() {
    return window.location.pathname.indexOf("/products/") !== -1;
  }

  function currentIsCollectionOrSearch() {
    const p = window.location.pathname;
    return p.indexOf("/collections/") !== -1 || p.indexOf("/search") !== -1;
  }

  function addNoIndexOnPaginated() {
    const params = new URLSearchParams(window.location.search);
    if (!params.has("page")) return;
    if (document.querySelector('meta[name="robots"][content="noindex"]')) return;

    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex";
    document.head.appendChild(meta);
  }

  // Mark that user visited a product page (used for restore behavior)
  if (currentIsProductPage()) {
    safeSetSession(STORAGE.productViewed, "1");
    return;
  }

  addNoIndexOnPaginated();

  // GLOBAL FLAGS to avoid duplicate init/reinit loops
  window.__INF_SCROLL_APPENDING__ = window.__INF_SCROLL_APPENDING__ || false;
  window.__INF_SCROLL_LAST_RESET__ = window.__INF_SCROLL_LAST_RESET__ || 0;

  class InfiniteScroll {
    constructor(config) {
      this.config = config;

      this.loading = false;
      this.page = this.getCurrentPageFromUrl();
      this.baseUrlNoPage = stripPageParam(window.location.href);

      this.productsSelector = config.productsSelector || null;
      this.paginationSelector = config.paginationSelector || null;

      this.productsEl = null;
      this.paginationEl = null;
      this.bottomEl = null;
      this.buttonEl = null;
      this.loaderImgEl = null;

      this.observer = null;
      this.boundScroll = null;

      // keep pagination hidden + survive AJAX rerenders
      this._paginationObserver = null;

      // abort controller to cancel old fetches on reset
      this._abort = null;

      this.init();
    }

    getCurrentPageFromUrl() {
      const params = new URLSearchParams(window.location.search);
      const p = parseInt(params.get("page") || "1", 10);
      return Number.isFinite(p) && p > 0 ? p : 1;
    }

    detectProductsContainer() {
      const candidates = [
        this.config.productsSelector,
        "#bc-sf-filter-products",
        ".product-grid",
        ".ProductList",
        ".collection-grid",
        ".products",
        ".product-loop",
        ".collection__products",
        '[data-section-type="collection-template"] .grid',
        "main .collection-grid__wrapper .grid",
        ".collection-grid__wrapper .grid.grid--uniform",
        ".collection-grid__wrapper .grid",
      ].filter(Boolean);

      for (const sel of candidates) {
        const el = document.querySelector(sel);
        if (
          el &&
          (el.querySelector('a[href*="/products/"]') ||
            el.querySelector('[class*="product"]'))
        ) {
          this.productsSelector = sel;
          return el;
        }
      }
      return null;
    }

    detectPagination() {
      const candidates = [
        this.config.paginationSelector,
        ".shopify-pagination",
        ".pagination",
        ".pagination-wrapper",
        '[class*="pagination"]',
        ".collection-grid__wrapper .pagination",
      ].filter(Boolean);

      for (const sel of candidates) {
        const el = document.querySelector(sel);
        if (
          el &&
          (el.querySelector('a[href*="page="]') ||
            el.querySelector(".next a") ||
            el.querySelector("a[rel='next']"))
        ) {
          this.paginationSelector = sel;
          return el;
        }
      }
      return null;
    }

    init() {
      if (!currentIsCollectionOrSearch()) return;

      this.resetIfNewCollectionUrl();

      this.productsEl = this.detectProductsContainer();
      this.paginationEl = this.detectPagination();

      if (!this.productsEl || !this.paginationEl) return;

      this.tryRestore();

      const storedPageNr = parseInt(safeGetSession(STORAGE.pageNr) || "", 10);
      if (storedPageNr === -1) {
        this.removeBottomUI();
        this.teardownPaginationObserver();
        return;
      }

      this.createBottomUI();
      this.keepPaginationHidden();

      if (this.config.mode === 1) this.attachAutoLoad();
      else this.attachButtonLoad();
    }

    resetIfNewCollectionUrl() {
      const storedUrl = safeGetSession(STORAGE.collectionUrl);
      const curr = stripPageParam(window.location.href);
      if (storedUrl && stripPageParam(storedUrl) !== curr) {
        safeSetSession(STORAGE.collectionUrl, curr);
        safeSetSession(STORAGE.collectionContent, "");
        safeSetSession(STORAGE.paginateContent, "");
        safeSetSession(STORAGE.pageNr, "1");
        safeSetSession(STORAGE.productViewed, "0");
      }
    }

    teardownPaginationObserver() {
      if (this._paginationObserver) {
        try {
          this._paginationObserver.disconnect();
        } catch (e) {}
        this._paginationObserver = null;
      }
    }

    tryRestore() {
      const productViewed = safeGetSession(STORAGE.productViewed);
      const storedUrl = safeGetSession(STORAGE.collectionUrl);
      const storedContent = safeGetSession(STORAGE.collectionContent);
      const storedPaginate = safeGetSession(STORAGE.paginateContent);
      const storedPageNr = safeGetSession(STORAGE.pageNr);

      const canonicalCurrent = stripPageParam(window.location.href);
      const canonicalStored = storedUrl ? stripPageParam(storedUrl) : null;

      if (productViewed !== "1") return;
      if (!canonicalStored || canonicalStored !== canonicalCurrent) return;
      if (!storedContent || !storedPaginate) return;

      this.productsEl.innerHTML = storedContent;
      this.paginationEl.innerHTML = storedPaginate;

      const pn = parseInt(storedPageNr || "1", 10);
      if (Number.isFinite(pn) && pn > 0) this.page = pn;

      safeSetSession(STORAGE.productViewed, "0");

      this.dispatchEvent("infinite-scroll:restored", { page: this.page });
      this.reInitIntegrations();
    }

    createBottomUI() {
      this.removeBottomUI();

      const bottom = document.createElement("div");
      bottom.id = "gscollectionbottom";
      bottom.style.cssText =
        "width:100%;text-align:center;margin:20px 0;clear:both;";

      if (this.config.mode === 2) {
        const btn = document.createElement("button");
        btn.id = "gsloadmore";
        btn.type = "button";
        btn.textContent = this.config.buttonText;
        btn.style.cssText =
          "max-width:250px;min-height:35px;cursor:pointer;padding:10px 20px;border:1px solid #ccc;border-radius:4px;background:#fff;font-size:16px;";

        const img = document.createElement("img");
        img.id = "gsloaderimg";
        img.src = this.config.loaderGif;
        img.alt = "Loading";
        img.style.cssText =
          "margin:10px auto 0px;display:none;max-width:200px;";

        bottom.appendChild(btn);
        bottom.appendChild(img);

        this.buttonEl = btn;
        this.loaderImgEl = img;
      } else {
        const img = document.createElement("img");
        img.id = "gsloaderimg";
        img.src = this.config.loaderGif;
        img.alt = "Loading";
        img.style.cssText = "margin-top:10px;display:block;max-width:200px;";
        bottom.appendChild(img);

        this.loaderImgEl = img;
      }

      if (this.paginationEl)
        this.paginationEl.insertAdjacentElement("afterend", bottom);
      else if (this.productsEl)
        this.productsEl.insertAdjacentElement("afterend", bottom);

      this.bottomEl = bottom;
    }

    removeBottomUI() {
      const old = document.getElementById("gscollectionbottom");
      if (old) old.remove();
      this.bottomEl = null;
      this.buttonEl = null;
      this.loaderImgEl = null;
    }

    hidePagination() {
      if (!this.paginationEl) return;
      this.paginationEl.style.display = "none";
    }

    keepPaginationHidden() {
      const hide = () => {
        const el = document.querySelector(
          this.paginationSelector || ".pagination"
        );
        if (el) el.style.display = "none";
      };

      hide();
      this.teardownPaginationObserver();

      this._paginationObserver = new MutationObserver(() => {
        hide();

        // if loader removed by theme DOM swap, recreate it
        if (!document.getElementById("gscollectionbottom")) {
          this.productsEl = this.detectProductsContainer() || this.productsEl;
          this.paginationEl = this.detectPagination() || this.paginationEl;
          if (this.productsEl && this.paginationEl) this.createBottomUI();
        }
      });

      const root =
        document.querySelector("#CollectionAjaxContent") || document.body;
      this._paginationObserver.observe(root, { childList: true, subtree: true });
    }

    attachAutoLoad() {
      if ("IntersectionObserver" in window && this.bottomEl) {
        this.bottomEl.getBoundingClientRect();

        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) this.loadNextPage();
            });
          },
          { root: null, rootMargin: "300px 0px", threshold: 0 }
        );

        setTimeout(() => {
          if (this.bottomEl) {
            this.observer.observe(this.bottomEl);
            this.checkIfNearBottom();
          }
        }, 150);

        return;
      }

      this.attachScrollFallback();
    }

    attachScrollFallback() {
      this.boundScroll = () => {
        if (this.loading) return;

        const scrollPosition = window.innerHeight + window.scrollY;
        const threshold = document.body.offsetHeight - 300;

        if (scrollPosition >= threshold) this.loadNextPage();
      };

      document.addEventListener("scroll", this.boundScroll);
      window.addEventListener("resize", this.boundScroll);
      window.addEventListener("orientationchange", this.boundScroll);
    }

    attachButtonLoad() {
      if (!this.buttonEl) return;
      this.buttonEl.addEventListener("click", () => this.loadNextPage());
    }

    buildUrlForPage(pageNumber) {
      const u = new URL(this.baseUrlNoPage, window.location.origin);
      const params = u.searchParams;
      params.set("page", String(pageNumber));
      u.search = params.toString() ? `?${params.toString()}` : "";
      return u.toString();
    }

    docHasNextPage(doc, nextPageNumber) {
      if (doc.querySelector('a[rel="next"]')) return true;

      const needle1 = `page=${nextPageNumber}`;
      const links = Array.from(doc.querySelectorAll("a[href]"));
      return links.some(
        (a) => (a.getAttribute("href") || "").indexOf(needle1) !== -1
      );
    }

    checkIfNearBottom() {
      if (!this.bottomEl || this.loading) return;

      const rect = this.bottomEl.getBoundingClientRect();
      const viewportHeight = window.innerHeight;

      if (rect.top - viewportHeight < 300) this.loadNextPage();
    }

    async loadNextPage() {
      if (this.loading) return;

      // re-detect in case theme replaced DOM via AJAX
      this.productsEl = this.detectProductsContainer() || this.productsEl;
      this.paginationEl = this.detectPagination() || this.paginationEl;
      if (!this.productsEl || !this.paginationEl) return;

      this.loading = true;

      if (this.config.mode === 2 && this.buttonEl) {
        this.buttonEl.disabled = true;
        this.buttonEl.textContent = "Loading...";
      }
      if (this.loaderImgEl && this.config.mode === 2) {
        this.loaderImgEl.style.display = "block";
      }

      const nextPage = this.page + 1;
      const nextUrl = this.buildUrlForPage(nextPage);

      try {
        // abort any previous fetch for this instance (safety)
        if (this._abort) {
          try {
            this._abort.abort();
          } catch (e) {}
        }
        this._abort = new AbortController();

        const res = await fetch(nextUrl, {
          credentials: "same-origin",
          cache: "no-store",
          signal: this._abort.signal,
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, "text/html");

        const nextProductsEl = this.productsSelector
          ? doc.querySelector(this.productsSelector)
          : null;
        const nextPaginationEl = this.paginationSelector
          ? doc.querySelector(this.paginationSelector)
          : null;

        if (!nextProductsEl || !nextProductsEl.innerHTML.trim()) {
          this.stopNoMorePages();
          return;
        }

        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = nextProductsEl.innerHTML;
        tempDiv.querySelectorAll("script, style").forEach((n) => n.remove());

        // prevent MutationObserver re-init loops while we append
        window.__INF_SCROLL_APPENDING__ = true;

        while (tempDiv.firstChild) {
          this.productsEl.appendChild(tempDiv.firstChild);
        }

        window.__INF_SCROLL_APPENDING__ = false;

        // init AjaxProduct only for newly added forms
        if (window.theme && theme.AjaxProduct) {
          this.productsEl
            .querySelectorAll(
              ".product-single__form:not(.ajax-product-initialized)"
            )
            .forEach(function (form) {
              new theme.AjaxProduct(form);
              form.classList.add("ajax-product-initialized");
            });
        }

        if (nextPaginationEl) {
          this.paginationEl.innerHTML = nextPaginationEl.innerHTML;
        }

        this.hidePagination();
        this.page = nextPage;

        this.persistState();
        this.reInitIntegrations();

        this.dispatchEvent("infinite-scroll:appended", {
          page: this.page,
          url: nextUrl,
        });

        const hasNext = this.docHasNextPage(doc, this.page + 1);
        if (!hasNext) this.stopNoMorePages();
      } catch (e) {
        // ignore abort errors as "normal"
        if (e && e.name === "AbortError") {
          // noop
        } else {
          this.stopNoMorePages();
          console.error("[InfiniteScroll] Failed:", e);
        }
      } finally {
        window.__INF_SCROLL_APPENDING__ = false;
        this.loading = false;

        if (this.config.mode === 2 && this.buttonEl) {
          this.buttonEl.disabled = false;
          this.buttonEl.textContent = this.config.buttonText;
        }
        if (this.loaderImgEl && this.config.mode === 2) {
          this.loaderImgEl.style.display = "none";
        }
      }
    }

    persistState() {
      safeSetSession(STORAGE.collectionUrl, this.baseUrlNoPage);
      safeSetSession(STORAGE.collectionContent, this.productsEl.innerHTML);
      safeSetSession(STORAGE.paginateContent, this.paginationEl.innerHTML);
      safeSetSession(STORAGE.pageNr, String(this.page));
      safeSetSession(STORAGE.productViewed, "0");
    }

    stopNoMorePages() {
      safeSetSession(STORAGE.pageNr, "-1");

      if (this._abort) {
        try {
          this._abort.abort();
        } catch (e) {}
        this._abort = null;
      }

      if (this.observer) {
        try {
          this.observer.disconnect();
        } catch (e) {}
        this.observer = null;
      }
      if (this.boundScroll) {
        document.removeEventListener("scroll", this.boundScroll);
        window.removeEventListener("resize", this.boundScroll);
        window.removeEventListener("orientationchange", this.boundScroll);
        this.boundScroll = null;
      }

      this.teardownPaginationObserver();
      this.removeBottomUI();
      this.dispatchEvent("infinite-scroll:ended", { page: this.page });
    }

    dispatchEvent(name, detail) {
      try {
        document.dispatchEvent(new CustomEvent(name, { detail }));
      } catch (e) {}
    }

    reInitIntegrations() {
      // Shopify Product Reviews (SPR)
      try {
        if (window.SPR && typeof window.SPR.initRatingHandler === "function")
          window.SPR.initRatingHandler();
        if (window.SPR && typeof window.SPR.initDomEls === "function")
          window.SPR.initDomEls();
        if (window.SPR && typeof window.SPR.loadBadges === "function")
          window.SPR.loadBadges();
      } catch (e) {}

      // Yotpo (safe)
      try {
        if (window.yotpo && window.Yotpo && window.Yotpo.API) {
          const api = new window.Yotpo.API(window.yotpo);
          if (api && typeof api.refreshWidgets === "function")
            api.refreshWidgets();
        }
      } catch (e) {}

      // Currency converters (Doubly)
      try {
        if (
          window.DoublyGlobalCurrency &&
          typeof window.DoublyGlobalCurrency.convertAll === "function"
        ) {
          const el = document.querySelector('[name="doubly-currencies"]');
          window.DoublyGlobalCurrency.convertAll(el ? el.value : undefined);
          setTimeout(() => {
            try {
              window.DoublyGlobalCurrency.convertAll(el ? el.value : undefined);
            } catch (e) {}
          }, 1000);
        }
      } catch (e) {}

      // Wishlist reload (Smart Wishlist pattern)
      try {
        if (
          typeof window.ReloadSmartWishlist !== "undefined" &&
          typeof window.ReloadSmartWishlist === "function"
        ) {
          window.ReloadSmartWishlist();
        }
      } catch (e) {}

      // Theme cart-drawer quick add (if your theme exposes it)
      try {
        if (
          window.HsCartDrawer &&
          typeof window.HsCartDrawer.quickBuyByttonByCollections === "function"
        ) {
          window.HsCartDrawer.quickBuyByttonByCollections(
            window.HsCartDrawer.json,
            window.HsCartDrawer.query
          );
        }
      } catch (e) {}

      // Generic: kick lazyload libraries
      try {
        window.dispatchEvent(new Event("resize"));
      } catch (e) {}
    }
  }

  const cfg = Object.assign(
    {
      mode: 1,
      loaderGif:
        "https://cdn.shopify.com/s/files/1/0644/2457/5191/files/loader.gif?v=1770197851",
      buttonText: "Load more",
      productsSelector: null,
      paginationSelector: null,
    },
    window.InfiniteScrollConfig || {}
  );

  window.initInfiniteScroll = function (forceReset = false) {
    if (!forceReset && window.__infiniteScrollInstance) return;

    if (forceReset && window.__infiniteScrollInstance) {
      try {
        const inst = window.__infiniteScrollInstance;

        if (inst) {
          if (inst._abort) {
            try {
              inst._abort.abort();
            } catch (e) {}
          }
          if (inst.observer) inst.observer.disconnect();

          if (inst.boundScroll) {
            document.removeEventListener("scroll", inst.boundScroll);
            window.removeEventListener("resize", inst.boundScroll);
            window.removeEventListener("orientationchange", inst.boundScroll);
          }

          if (inst.teardownPaginationObserver) inst.teardownPaginationObserver();
        }
      } catch (e) {}
      window.__infiniteScrollInstance = null;
    }

    window.__infiniteScrollInstance = new InfiniteScroll(cfg);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      window.initInfiniteScroll();
    });
  } else {
    window.initInfiniteScroll();
  }

  // Re-init only when filters replace content, NOT when infinite scroll appends
  (function bindReInitOnAjaxCollectionChange() {
    const target = document.querySelector("#CollectionAjaxContent");
    if (!target) return;

    let t;
    const obs = new MutationObserver(() => {
      // ignore changes caused by infinite scroll itself
      if (window.__INF_SCROLL_APPENDING__) return;

      // throttle + avoid rapid-fire resets
      const now = Date.now();
      if (now - window.__INF_SCROLL_LAST_RESET__ < 500) return;

      clearTimeout(t);
      t = setTimeout(() => {
        if (window.__INF_SCROLL_APPENDING__) return;
        window.__INF_SCROLL_LAST_RESET__ = Date.now();
        if (window.initInfiniteScroll) window.initInfiniteScroll(true);
      }, 200);
    });

    obs.observe(target, { childList: true, subtree: true });
  })();
})();